import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useSave } from "@ncp/ui";
import type { ReportResult } from "@ncp/screens";
import type { BaseBody } from "../generic/api";

export type ReportFn = "count" | "sum" | "avg" | "min" | "max";
export const REPORT_FNS: ReportFn[] = ["count", "sum", "avg", "min", "max"];
export const REPORT_CHARTS = ["table", "bar", "line", "pie", "kpi"] as const;

export interface ReportMeasure { key: string; label: string; labelAr?: string | null; fn: ReportFn; field?: string | null }
export interface ReportColumnSpec { field: string; label?: string | null; labelAr?: string | null }

export interface ReportBody extends BaseBody {
  source: "records" | "cases";
  sourceKey: string;
  filters: unknown;
  groupBy: string[];
  measures: ReportMeasure[];
  columns: ReportColumnSpec[];
  chart: (typeof REPORT_CHARTS)[number];
  departmentKey?: string | null;
  includeTest: boolean;
}

export function newReportBody(name: string, nameAr: string | null, extra: Record<string, string>): ReportBody {
  return { name, nameAr, description: null, descriptionAr: null, tags: [], source: (extra.source as "records" | "cases") || "records", sourceKey: extra.sourceKey ?? "", filters: [], groupBy: [], measures: [], columns: [], chart: "table", departmentKey: null, includeTest: false };
}

/** Case fields every service's cases carry, for filters and columns of case reports. */
export const CASE_FIELDS = ["caseNumber", "title", "state", "status", "priority", "createdAt", "workspaceKey", "isTest"] as const;

/** A preview stores nothing; the designer shows its failure in place (`saveFailureText`), hence silent. */
export function useReportPreview() {
  const api = useApi();
  return useSave({ mutationFn: (body: ReportBody & { key?: string }) => api.post<ReportResult>("/api/platform/reports/preview", { body }), report: "silent" });
}

export function useReportRun(key: string | undefined, version?: number, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["reports", "run", key, version], queryFn: () => api.get<ReportResult>(`/api/platform/reports/${encodeURIComponent(key!)}/run${version ? `?version=${version}` : ""}`), enabled: !!key && enabled, retry: false });
}

export function useReportExport() {
  const api = useApi();
  return (key: string, format: "csv" | "xlsx", locale: string, version?: number) =>
    api.download(`/api/platform/reports/${encodeURIComponent(key)}/export?format=${format}&locale=${locale}${version ? `&version=${version}` : ""}`, `${key}.${format}`);
}
